(() => {
  const elementName = 'import-charges-acknowledgement';

  if (customElements.get(elementName)) return;

  class ImportChargesAcknowledgement extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('[data-import-charges-acknowledgement]');
      this.scope = this.closest(
        '.xo-product-info-content__buy-form, .xo-product-info-media-slide__action'
      );
      this.product = this.closest('xo-product');

      if (!this.checkbox || !this.scope) return;

      this.acceleratedCheckoutStates = new WeakMap();

      this.handleChange = () => this.syncControls();

      this.handleClick = (event) => {
        if (this.checkbox.checked || !(event.target instanceof Element)) return;

        const acceleratedCheckout = event.target.closest(
          '.xo-product-info-content__buy-now, .xo-product-info-media-slide__buy-now'
        );

        if (!acceleratedCheckout || !this.scope.contains(acceleratedCheckout)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.checkbox.focus();
        this.checkbox.reportValidity();
      };

      this.handleSubmit = (event) => {
        if (this.checkbox.checked) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.checkbox.focus();
        this.checkbox.reportValidity();
      };

      this.checkbox.addEventListener('change', this.handleChange);
      this.scope.addEventListener('click', this.handleClick, true);
      this.scope.addEventListener('submit', this.handleSubmit, true);

      this.controlObserver = new MutationObserver(() => this.syncControls());
      this.controlObserver.observe(this.product || this.scope, {
        attributes: true,
        attributeFilter: ['xo-disabled'],
        childList: true,
        subtree: true,
      });

      this.syncControls();
    }

    disconnectedCallback() {
      this.checkbox?.removeEventListener('change', this.handleChange);
      this.scope?.removeEventListener('click', this.handleClick, true);
      this.scope?.removeEventListener('submit', this.handleSubmit, true);
      this.controlObserver?.disconnect();
    }

    getButtons() {
      const buttons = Array.from(
        this.scope.querySelectorAll('#AddToCartTrigger, xo-cart-add button')
      );

      if (this.product) {
        const stickyButtons = [
          this.product.querySelector('#ToggleAtcSticky'),
          this.product.querySelector('#ToggleAtcStickyDesktop'),
        ].filter(Boolean);

        buttons.push(...stickyButtons);
      }

      return [...new Set(buttons)];
    }

    isProductUnavailable(button) {
      const cartAdd = button.closest('xo-cart-add');

      if (cartAdd?.hasAttribute('xo-disabled')) return true;

      if (
        button.matches(
          '#AddToCartTrigger, #ToggleAtcSticky, #ToggleAtcStickyDesktop'
        )
      ) {
        return Boolean(this.scope.querySelector('xo-cart-add[xo-disabled]'));
      }

      return false;
    }

    getAcceleratedCheckoutContainers() {
      return Array.from(
        this.scope.querySelectorAll(
          '.xo-product-info-content__buy-now, .xo-product-info-media-slide__buy-now'
        )
      );
    }

    setButtonLocked(button, locked) {
      if (locked) {
        if (button.disabled) return;

        button.disabled = true;
        button.setAttribute('data-import-ack-disabled', '');
        return;
      }

      if (button.hasAttribute('data-import-ack-disabled')) {
        button.disabled = false;
        button.removeAttribute('data-import-ack-disabled');
      }
    }

    setAcceleratedCheckoutLocked(container, locked) {
      if (locked) {
        if (!this.acceleratedCheckoutStates.has(container)) {
          const alreadyManaged = container.hasAttribute('data-import-ack-locked');

          this.acceleratedCheckoutStates.set(container, {
            hadInert: alreadyManaged ? false : container.hasAttribute('inert'),
            ariaHidden: alreadyManaged ? null : container.getAttribute('aria-hidden'),
          });
        }

        container.setAttribute('data-import-ack-locked', '');
        container.setAttribute('inert', '');
        container.setAttribute('aria-hidden', 'true');
        return;
      }

      if (container.hasAttribute('data-import-ack-locked')) {
        const originalState = this.acceleratedCheckoutStates.get(container);

        container.removeAttribute('data-import-ack-locked');

        if (originalState?.hadInert) {
          container.setAttribute('inert', '');
        } else {
          container.removeAttribute('inert');
        }

        if (
          originalState?.ariaHidden === null ||
          originalState?.ariaHidden === undefined
        ) {
          container.removeAttribute('aria-hidden');
        } else {
          container.setAttribute('aria-hidden', originalState.ariaHidden);
        }

        this.acceleratedCheckoutStates.delete(container);
      }
    }

    syncControls() {
      const locked = !this.checkbox.checked;

      this.getButtons().forEach((button) =>
        this.setButtonLocked(
          button,
          locked || this.isProductUnavailable(button)
        )
      );

      this.getAcceleratedCheckoutContainers().forEach((container) =>
        this.setAcceleratedCheckoutLocked(container, locked)
      );
    }
  }

  customElements.define(elementName, ImportChargesAcknowledgement);
})();